// reactionAlert — "Sara reacted 😂 to your message", decided from one realtime
// UPDATE payload.
//
// Contract item 5 (Umer, 2026-09-14): a reaction on MY message raises the same
// in-tab alert + sound an order-chat MESSAGE raises. The alert itself is fired
// by components/NotificationHandler.tsx; everything that decides *whether* to
// fire lives here, pure, for two reasons:
//   • the group-chat (`room_messages`) half of item 5 is built by another
//     workstream in the same file — a decision helper in its own module is a
//     merge that cannot conflict, and
//   • "did this payload add a reaction, and was it mine to hear about" is
//     exactly the logic that is impossible to eyeball in a realtime callback.
//
// `messages` carries REPLICA IDENTITY FULL (migration 0025), so an UPDATE
// payload has both `old` and `new` — the diff below is the reason that matters
// here. Without `old` (a re-fetch, a payload without it) we deliberately decide
// NOTHING rather than guess: a re-announced reaction is worse than a missed one.

import { reactionAlertText } from './reactionActors';

export type ReactionMap = Record<string, string[]>;

export interface ReactionAdd {
  emoji: string;
  userId: string;
}

/**
 * Every (emoji, user) pair present in `after` and absent from `before`.
 * Removals and re-orderings are ignored — only an ADDED reaction is an event.
 *
 * NOT the same function as `services/roomAlerts.diffReactionAdds`, despite the
 * name: see the long note there. `messages` carries `old` (0025), so a missing
 * `before` means "it had none" and everything in `after` is new; the group
 * table has no `old` and treats a missing `prev` as "first sight → nothing new".
 */
export function diffReactionAdds(
  before: ReactionMap | null | undefined,
  after: ReactionMap | null | undefined,
): ReactionAdd[] {
  const out: ReactionAdd[] = [];
  // Map-of-Sets rather than a joined key: an emoji is arbitrary text and a
  // separator that is safe against every emoji and every user id does not
  // exist worth arguing about.
  const had = new Map<string, Set<string>>();
  for (const [emoji, ids] of Object.entries(before ?? {})) {
    had.set(emoji, new Set(ids ?? []));
  }
  for (const [emoji, ids] of Object.entries(after ?? {})) {
    const seen = had.get(emoji);
    for (const uid of ids ?? []) {
      if (!seen || !seen.has(uid)) out.push({ emoji, userId: uid });
    }
  }
  return out;
}

export interface ReactionAlertInput {
  /** `payload.old.reactions` — null/undefined simply means "had none". */
  before: ReactionMap | null | undefined;
  /**
   * False when the payload carried no `old` ROW at all (a re-fetch, a table
   * without REPLICA IDENTITY FULL). Then there is no diff to trust and we
   * decide nothing: re-announcing an old reaction is worse than missing one.
   * Defaults to true, because `messages` does carry `old` (migration 0025).
   */
  beforeKnown?: boolean;
  /** `payload.new.reactions`. */
  after: ReactionMap | null | undefined;
  /** The reacted-to message. */
  message: { senderId?: string | null; body?: string | null; channel?: string | null };
  /** Me. An alert is only ever about MY message. */
  viewerId: string | undefined;
  /** AppContext user lookup; a stranger falls back to "Someone". */
  nameOf?: (userId: string) => string | undefined;
}

export interface ReactionAlertDecision {
  actorId: string;
  actorName: string;
  emoji: string;
  /** "Sara reacted 😂 to your message" — one sentence, shared with mobile push. */
  text: string;
}

/**
 * Should this payload raise the reaction alert, and what does it say?
 *
 * Fires only when: the message is mine, someone ELSE added a reaction, and the
 * channel is an in-app one. The customer channel is SMS — it cannot carry a
 * reaction (0058 forbids it outright), so a change there is never an alert.
 * Returns the newest add when several land in one payload, so a burst reads as
 * one sentence rather than a stack of toasts.
 */
export function decideReactionAlert(input: ReactionAlertInput): ReactionAlertDecision | null {
  const { before, after, message, viewerId } = input;
  if (!viewerId) return null;
  if (input.beforeKnown === false) return null; // no `old` → no reliable diff
  if (!message.senderId || message.senderId !== viewerId) return null;
  if (message.channel === 'customer') return null;

  const adds = diffReactionAdds(before, after).filter((a) => a.userId !== viewerId);
  if (!adds.length) return null;
  const add = adds[adds.length - 1];
  const actorName = input.nameOf?.(add.userId) || 'Someone';
  return {
    actorId: add.userId,
    actorName,
    emoji: add.emoji,
    text: reactionAlertText(actorName, add.emoji),
  };
}
